/**
 * Organization API Service
 * 組織管理 API 服務
 */
import apiClient from '../client';

export interface Organization {
  id: number;
  org_code: string;
  org_name: string;
  org_type: 'company' | 'government' | 'ngo';
  contact_person?: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  subscription_plan: 'basic' | 'professional' | 'enterprise';
  subscription_start_date?: string;
  subscription_end_date?: string;
  max_users: number;
  max_projects: number;
  max_storage_gb: number;
  status: 'active' | 'suspended' | 'inactive';
  is_system_management: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateOrganizationRequest {
  org_code: string;
  org_name: string;
  org_type: 'company' | 'government' | 'ngo';
  contact_person?: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  subscription_plan?: 'basic' | 'professional' | 'enterprise';
  max_users?: number;
  max_projects?: number;
  max_storage_gb?: number;
}

export interface UpdateOrganizationRequest {
  org_name?: string;
  org_type?: 'company' | 'government' | 'ngo';
  contact_person?: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  subscription_plan?: 'basic' | 'professional' | 'enterprise';
  max_users?: number;
  max_projects?: number;
  max_storage_gb?: number;
  status?: 'active' | 'suspended' | 'inactive';
}

export interface OrganizationListResponse {
  total: number;
  items: Organization[];
}

export interface OrganizationStatsResponse {
  organization_id: number;
  organization_name: string;
  total_users: number;
  active_users: number;
  total_projects: number;
  active_projects: number;
  storage_used_gb: number;
  user_quota_usage: number;
  project_quota_usage: number;
  storage_quota_usage: number;
}

class OrganizationService {
  /**
   * 獲取組織列表
   */
  async getOrganizations(params?: {
    skip?: number;
    limit?: number;
    status?: string;
  }): Promise<OrganizationListResponse> {
    const response = await apiClient.get<OrganizationListResponse>(
      '/api/v1/organizations',
      { params }
    );
    return response.data;
  }

  /**
   * 獲取單個組織
   */
  async getOrganization(id: number): Promise<Organization> {
    const response = await apiClient.get<Organization>(
      `/api/v1/organizations/${id}`
    );
    return response.data;
  }

  /**
   * 創建組織 (僅 system_admin)
   */
  async createOrganization(data: CreateOrganizationRequest): Promise<Organization> {
    const response = await apiClient.post<Organization>(
      '/api/v1/organizations',
      data
    );
    return response.data;
  }

  /**
   * 更新組織
   */
  async updateOrganization(
    id: number,
    data: UpdateOrganizationRequest
  ): Promise<Organization> {
    const response = await apiClient.put<Organization>(
      `/api/v1/organizations/${id}`,
      data
    );
    return response.data;
  }

  /**
   * 刪除組織 (僅 system_admin)
   */
  async deleteOrganization(id: number): Promise<void> {
    await apiClient.delete(`/api/v1/organizations/${id}`);
  }

  /**
   * 獲取組織統計資料
   */
  async getOrganizationStats(id: number): Promise<OrganizationStatsResponse> {
    const response = await apiClient.get<OrganizationStatsResponse>(
      `/api/v1/organizations/${id}/stats`
    );
    return response.data;
  }
}

export default new OrganizationService();
